import {
  applyMarketOutcomePrices,
  pricesFromBatchPriceMap,
} from '@/domain/market/market-price-map.js';
import {
  applyCollectionExclusiveOutcomePrices,
  looksLikeDefaultHalf,
} from '@/domain/market/exclusive-outcome-odds.js';
import { resolveMarketTradeSymbol } from '@/utils/market-detail-map';
import { isListedMarket } from '@/utils/market-list-status.js';
import { isMarketResolved, parseMarketOutcomes } from '@/utils/market-outcomes';

/** 单次 Feed 批量询价最多条目（symbol×outcome），避免拖慢后台请求 */
export const FEED_BATCH_PRICE_MAX_ITEMS = 80;

/** 分片大小：串行多片，单片失败不影响已写入 */
export const FEED_BATCH_PRICE_CHUNK_SIZE = 40;

/**
 * 从 Feed rawRows 收集 batchPrice 请求项（按列表顺序，优先首屏可见）。
 * @param {object[]} rawRows
 * @param {{ maxItems?: number }} [options]
 * @returns {Array<{ symbol: string, outcome: string }>}
 */
export function collectFeedBatchPriceItems(rawRows, options = {}) {
  const maxItems = Number(options.maxItems);
  const limit =
    Number.isFinite(maxItems) && maxItems > 0 ? Math.floor(maxItems) : FEED_BATCH_PRICE_MAX_ITEMS;

  const items = [];
  const seen = new Set();
  const rows = Array.isArray(rawRows) ? rawRows : [];

  for (const row of rows) {
    if (row?.marketPreview === true || row?.sportsFeed === true) continue;
    const markets = Array.isArray(row?.markets) ? row.markets : [];
    for (const market of markets) {
      if (!isListedMarket(market)) continue;
      if (isMarketResolved(market, row)) continue;
      const symbol = resolveMarketTradeSymbol(market);
      const outcomes = parseMarketOutcomes(market?.outcomes);
      if (!symbol || outcomes.length < 2) continue;

      for (const outcome of outcomes) {
        const label = String(outcome ?? '').trim();
        if (!label) continue;
        const key = `${symbol}\u0001${label}`;
        if (seen.has(key)) continue;
        seen.add(key);
        items.push({ symbol, outcome: label });
        if (items.length >= limit) return items;
      }
    }
  }

  return items;
}

/**
 * 将 batchPrice Map 写回 rawRows 内 market.outcomePrices（原地）。
 * @param {object[]} rawRows
 * @param {Record<string, string|number>|null|undefined} batchMap
 * @returns {boolean} 是否有任意写回
 */
export function applyBatchPriceMapToFeedRows(rawRows, batchMap) {
  if (!batchMap || typeof batchMap !== 'object') return false;
  const rows = Array.isArray(rawRows) ? rawRows : [];
  let any = false;

  for (const row of rows) {
    if (row?.marketPreview === true || row?.sportsFeed === true) continue;
    const markets = Array.isArray(row?.markets) ? row.markets : [];
    const exclusiveSiblingCount = markets.filter((market) => {
      if (isMarketResolved(market, row)) return false;
      return parseMarketOutcomes(market?.outcomes).length === 2;
    }).length;
    const skipDefaultHalf = exclusiveSiblingCount >= 2;

    for (const market of markets) {
      if (!isListedMarket(market)) continue;
      const symbol = resolveMarketTradeSymbol(market);
      if (!symbol) continue;
      const prices = pricesFromBatchPriceMap(batchMap, symbol, market.outcomes);
      if (
        skipDefaultHalf &&
        Array.isArray(prices) &&
        prices.length >= 2 &&
        looksLikeDefaultHalf(prices[0]) &&
        looksLikeDefaultHalf(prices[1])
      ) {
        continue;
      }
      if (applyMarketOutcomePrices(market, prices, row)) any = true;
    }
    if (applyCollectionExclusiveOutcomePrices(markets, row)) any = true;
  }

  return any;
}

/**
 * 按 chunk 切分 batchPrice items。
 * @param {Array<{ symbol: string, outcome: string }>} items
 * @param {number} [chunkSize]
 */
export function chunkFeedBatchPriceItems(items, chunkSize = FEED_BATCH_PRICE_CHUNK_SIZE) {
  const list = Array.isArray(items) ? items : [];
  const size =
    Number.isFinite(Number(chunkSize)) && Number(chunkSize) > 0
      ? Math.floor(Number(chunkSize))
      : FEED_BATCH_PRICE_CHUNK_SIZE;
  const out = [];
  for (let i = 0; i < list.length; i += size) {
    out.push(list.slice(i, i + size));
  }
  return out;
}
